import { maybe, rd, type RemoteData } from '@passionware/monads';
import { zip } from 'lodash';
import { Plus } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type {
  EnterpriseTeam,
  EnterpriseWorkspace
} from 'v5/api/enterprise/enterprise.api';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import {
  teamSlugFromSettingsPath,
  workspaceSlugFromSettingsPath
} from 'v5/core/app-scope/resolveSettingsAppScopeFromPathname';
import { appRouting } from 'v5/core/routing/appRouting';
import { EnterpriseNewTeamMenuItem } from 'v5/features/core/enterprise/EnterpriseNewTeamMenuItem';
import { EnterpriseSidebarEmptyState } from 'v5/features/core/enterprise/EnterpriseSidebarEmptyState';
import { EnterpriseTeamIcon } from 'v5/features/core/enterprise/EnterpriseTeamIcon';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { renderEnterpriseTeamSettingsItems } from 'v5/features/core/enterprise/renderEnterpriseTeamSettingsItems';
import { renderEnterpriseWorkspaceSettingsItems } from 'v5/features/core/enterprise/renderEnterpriseWorkspaceSettingsItems';
import { EnterpriseMenuCollapsibleSkeleton } from 'v5/features/core/enterprise/EnterpriseMenuCollapsibleSkeleton';
import {
  MenuCollapsible,
  MenuItem,
  MenuSeparator,
  menuPaddingLeft
} from 'v5/patterns/sidebar/Menu';
import { SidebarSectionHeader } from 'v5/patterns/sidebar/Sidebar';
import { ifCan } from 'v5/platform/react/ifCan';
import { WithServices } from 'v5/platform/ts/services';
import {
  AccessQuery,
  WithAccessService
} from 'v5/services/front/AccessService/AccessService';
import { isAccessProbePending } from 'v5/services/front/AccessService/isAccessProbePending';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

export type EnterpriseWorkspaceSettingsCollapsibleProps = WithServices<
  [
    WithEnterpriseService,
    WithUserFlowService,
    WithAppScopeService,
    WithAccessService
  ]
> & {
  workspace: EnterpriseWorkspace;
  organizationId: string;
  canManage?: boolean;
};

/**
 * Must render under a workspace AppScopeProvider so create-team / slug scope
 * follow that workspace. `useCan` identities come from the workspace and team
 * rows, not from context.
 */
export function EnterpriseWorkspaceSettingsCollapsible({
  services,
  workspace,
  organizationId,
  canManage = false
}: EnterpriseWorkspaceSettingsCollapsibleProps) {
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const { t: tResourceTypes } = useTranslation('core', {
    keyPrefix: 'resourceTypesAdmin'
  });
  const { pathname } = useLocation();
  const slugAppScope = services.appScopeService.useSlugAppScope();
  const settingsWorkspaceSlug = workspaceSlugFromSettingsPath(pathname);
  const settingsTeamSlug = teamSlugFromSettingsPath(pathname);

  const canRd = services.accessService.useCan({
    level: 'workspace',
    organizationId,
    workspaceId: workspace.id
  });
  const workspaceFeaturesPending = isAccessProbePending(canRd);

  const routing = appRouting.org(organizationId);
  const wsSettings = routing.settings.workspace(workspace.slug);

  // Only auto-expand when browsing that workspace's settings (`/settings/w/...`).
  // Do not expand from the org-level “default workspace for links” fallback — that
  // makes Global settings pages open a huge tree and the sidebar height jump.
  const isActiveWorkspace =
    settingsWorkspaceSlug != null && workspace.slug === settingsWorkspaceSlug;

  const isInWorkspaceSettings = settingsWorkspaceSlug === workspace.slug;
  const [isOpen, setIsOpen] = useState(isActiveWorkspace);

  const teamsRd = services.enterpriseService.useTeams(
    isInWorkspaceSettings || isOpen
      ? maybe.of({
          organizationId,
          workspaceId: workspace.id
        })
      : maybe.ofAbsent()
  );
  const loadedTeams = rd.tryGet(teamsRd) ?? [];
  const teamCans = services.accessService.useCanQueries(
    loadedTeams.map(team =>
      appScopeUtils.identityFromTeam(organizationId, team)
    )
  );

  const handleCreateTeam = () => {
    services.userFlowService.openCreateEnterpriseTeamDialog({
      organizationId,
      workspaceId: workspace.id,
      appScope: slugAppScope
    });
  };

  const newTeamItem = canManage ? (
    <EnterpriseNewTeamMenuItem onClick={handleCreateTeam} />
  ) : null;

  const disabledNewTeamItem = (
    <MenuItem
      level={1}
      disabled
      className="text-fg-muted"
      icon={<Plus className="size-4" />}
    >
      {t('newTeam')}
    </MenuItem>
  );

  return (
    <MenuCollapsible
      icon={
        <EnterpriseWorkspaceIcon
          name={workspace.name}
          iconUrl={workspace.iconUrl}
        />
      }
      label={workspace.name}
      defaultOpen={isActiveWorkspace}
      expandOnChildSelect
      onOpenChange={setIsOpen}
    >
      {workspaceFeaturesPending ? (
        <EnterpriseMenuCollapsibleSkeleton count={4} level={1} />
      ) : (
        ifCan
          .ready(canRd)
          .render(can =>
            renderEnterpriseWorkspaceSettingsItems(wsSettings, 1, {
              can,
              resourceTypesLabel: tResourceTypes('workspaceTitle')
            })
          )
          .otherwise(null)
      )}

      {rd
        .journey(teamsRd)
        .wait(null)
        .catch(() => (
          <div
            className="py-2 pr-3 text-[13px] text-fg-muted"
            style={{ paddingLeft: menuPaddingLeft(1) }}
          >
            {t('teamsUnavailable')}
          </div>
        ))
        .map(teams => {
          if (teams.length === 0) {
            if (!newTeamItem) {
              return null;
            }
            return (
              <>
                <MenuSeparator level={1} className="my-1 mx-3" />
                <SidebarSectionHeader level={1}>
                  {t('teamsSettingsLabel')}
                </SidebarSectionHeader>
                <EnterpriseSidebarEmptyState
                  messageKey="emptyTeams"
                  level={1}
                  className="py-1"
                />
                {newTeamItem}
              </>
            );
          }

          return ifCan
            .union()
            .render(parentCan => (
              <>
                <MenuSeparator level={1} className="my-1 mx-3" />
                <SidebarSectionHeader level={1}>
                  {t('teamsSettingsLabel')}
                </SidebarSectionHeader>
                {zip(teams, teamCans).map(([team, teamCan]) => {
                  if (maybe.isAbsent(team) || maybe.isAbsent(teamCan)) {
                    return null;
                  }
                  return (
                    <Fragment key={team.id}>
                      {renderTeamSettingsCollapsible(parentCan, teamCan, {
                        team,
                        workspaceSlug: workspace.slug,
                        organizationId,
                        defaultOpen:
                          isActiveWorkspace && settingsTeamSlug === team.slug
                      })}
                    </Fragment>
                  );
                })}
                {newTeamItem ?? disabledNewTeamItem}
              </>
            ))
            .otherwise(null);
        })}
    </MenuCollapsible>
  );
}

function renderTeamSettingsCollapsible(
  parentCan: () => boolean,
  teamCan: (query: AccessQuery) => RemoteData<boolean>,
  {
    team,
    workspaceSlug,
    organizationId,
    defaultOpen
  }: {
    team: EnterpriseTeam;
    workspaceSlug: string;
    organizationId: string;
    defaultOpen: boolean;
  }
) {
  const settings = appRouting
    .org(organizationId)
    .settings.workspace(workspaceSlug)
    .team(team.slug);

  return ifCan
    .childOf(parentCan)
    .ready(teamCan)
    .render(can => (
      <MenuCollapsible
        level={1}
        icon={
          <EnterpriseTeamIcon
            name={team.name}
            iconUrl={team.iconUrl}
            iconColor={team.iconColor}
          />
        }
        label={team.name}
        defaultOpen={defaultOpen}
        expandOnChildSelect
      >
        {renderEnterpriseTeamSettingsItems(team, settings, can)}
      </MenuCollapsible>
    ))
    .otherwise(null);
}
